import { config as expensesСonfig } from "components/expenses/formConfig";
import { config as cargoOwnerShareAccessConfig } from "components/shared/cargoOwnerShareAccess/config";
import SelectMenuActionButton from "components/shared/selectMenuActionButton";
import { counterpartyStatusBadge } from "helpers/bid";
import {
    bidPaymentStatuses,
    bidPaymentStatusesEnum,
    bidPointTypesEnum,
    bidRoles,
    documentsTrackingStatuses,
    documentsTrackingStatusesEnum,
    invoiceTriggerTypes,
    paymentPeriodTypes,
} from "helpers/enums";
import { bidStatuses, bidStatusesEnum } from "helpers/enums/bidStatuses";
import { cargo, point, truck } from "helpers/models";
import { requiredField } from "helpers/validators";
import compact from "lodash/compact";
import policy from "policies";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import { components } from "react-select";
import { selectList as cargoOwnersList } from "sources/books/cargoOwners";
import { listSalesManagers, list as logistsList } from "sources/books/employees";
import { list as legalPersons } from "sources/books/legalPersons";
import { list as truckingPartnersList } from "sources/books/truckingPartners";
import { getListForSelect as userGroupsList } from "sources/books/usersGroups";

const { contactPerson: cargoOwnerContactPerson, ...cargoOwnerConfig } = cargoOwnerShareAccessConfig;

export const CargoOwnerOption = (props) => {
    const { name, status, inn, kpp } = props.data.option;
    return (
        <components.Option {...props}>
            {inn || kpp ? (
                <>
                    {name} <small className="text-muted">({compact([inn, kpp]).join("/")})</small>
                </>
            ) : (
                name
            )}
            &nbsp;
            {counterpartyStatusBadge(status)}
        </components.Option>
    );
};

export const CargoOwnerValueContainer = (props) => {
    const { children, getValue, hasValue } = props;
    if (!hasValue) {
        return <components.ValueContainer {...props} />;
    }
    const value = getValue()[0];
    if (!value || !value.option) {
        return <components.ValueContainer {...props} />;
    }
    const { name, inn, kpp } = value.option;
    return (
        <components.ValueContainer {...props}>
            {!props.selectProps.inputValue && (
                <components.SingleValue {...props}>
                    {name} <small>({compact([inn, kpp]).join("/")})</small>
                </components.SingleValue>
            )}
            {children[children.length - 1]}
        </components.ValueContainer>
    );
};

export const CargoOwnerMenuList = (props) => {
    const {
        children,
        selectProps: { modal, organization, roles, onChange, profile, inputProps },
    } = props;
    const canCreate = useMemo(() => {
        return policy("user", {
            user: {
                canEditAll: !organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: organization.intBooksEditPermissionsFlags,
                roles,
            },
            record: {},
            options: { book: "cargoowners" },
        }).canEditBook();
    }, [organization, roles]);
    const handleCreateCargoOwner = () => {
        if (modal) {
            modal({
                name: "createCargoOwner",
                windowMod: "wide",
                title: "Создание контрагента",
                profile,
                onSuccess: (cargoOwner) => {
                    onChange({
                        name: inputProps.name,
                        value: cargoOwner.id,
                        option: cargoOwner,
                    });
                },
            });
        }
    };
    return (
        <>
            <components.MenuList {...props}>{children}</components.MenuList>
            {canCreate && (
                <div className="text-center border-top">
                    <SelectMenuActionButton onAction={handleCreateCargoOwner}>
                        Создать контрагента
                    </SelectMenuActionButton>
                </div>
            )}
        </>
    );
};

const cargoTruckConfig = {
    status: {
        // select
        label: "Статус",
        required: true,
        inputProps: { name: "status", disabled: false },
        options: bidStatuses
            .filter((x) => x.value !== bidStatusesEnum.Planned)
            .sort((a, b) => {
                if (a.value === bidStatusesEnum.Canceled) {
                    return 1;
                } else {
                    return b.value === bidStatusesEnum.Canceled ? -1 : 0;
                }
            }),
    },
    extendedProperties: [],
    driver: { value: {}, validators: [requiredField] },
    secondaryDriver: {},
    isDriverForwarder: {
        label: "Водитель-экспедитор",
        inputProps: { name: "isDriverForwarder" },
        type: "checkbox",
    },
    carOption: {
        value: truck,
        validators: [requiredField],
    },
    trailerOption: {},
    createdById: {},
    driverPaymentTypeId: {
        // select
        label: "Тип оплаты",
        inputProps: { name: "driverPaymentTypeId" },
        dictionary: "paymentType",
    },
    driverNdsTypeId: {
        // select
        label: "НДС",
        inputProps: { name: "driverNdsTypeId" },
        dictionary: "nDSType",
    },
    driverHourlyPrice: {
        // field
        label: "Фиксированная цена",
        inputProps: { name: "driverHourlyPrice", type: "number" },
    },
    driverPrice: {
        // field
        label: "Почасовая цена",
        inputProps: { name: "driverPrice", type: "number" },
    },
    route: {}, // fully custom component
};

const cargoConfig = {
    name: {
        // field
        label: "Наименование груза",
        inputProps: { name: "name" },
    },
    typeId: {
        // select
        label: "Тип товара",
        inputProps: { name: "typeId" },
        dictionary: "cargoType",
        relatedFields: true,
    },
    comment: {
        // field
        label: "Комментарий",
        textarea: true,
        inputProps: { name: "comment" },
    },
    packType: {
        // field
        label: "Тип упаковки",
        inputProps: { name: "packType" },
    },
    placesCount: {
        // field
        label: "Количество мест",
        inputProps: { name: "placesCount", type: "number", step: "1", min: "0", max: "32767" },
    },
    weight: {
        // field
        label: "Вес, т",
        inputProps: { name: "weight", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    volume: {
        // field
        label: "Объем, м³",
        inputProps: { name: "volume", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    length: {
        // field
        label: "Длина, м",
        inputProps: { name: "length", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    height: {
        // field
        label: "Высота, м",
        inputProps: { name: "height", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    width: {
        // field
        label: "Ширина, м",
        inputProps: { name: "width", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    extendedProperties: [],
    typeOptions: {
        value: [],
        config: {
            id: {},
            entityOptionId: {},
        },
    },
};

const mainConfig = {
    isEmptyMileageBid: {
        label: "Порожняя заявка",
        inputProps: { name: "isEmptyMileageBid" },
    },
    isExpressBid: {
        label: "Экспресс-заявка",
        inputProps: { name: "isExpressBid" },
        type: "checkbox",
    },
    showDraftInPlanning: {
        label: "Отображать черновик в планировании",
        inputProps: { name: "showDraftInPlanning" },
        type: "checkbox",
    },
    createDocumentAssignment: {
        label: "Создать водителю задачу по сдаче документов",
        inputProps: { name: "createDocumentAssignment" },
        type: "checkbox",
    },
    contractId: {
        label: "Договор с контрагентом",
        inputProps: { name: "contractId" },
        propName: "name",
    },
    contractNumber: {
        label: "Номер договора с контрагентом",
        inputProps: { name: "contractNumber" },
    },
    temperatureRegime: {
        config: {
            temperatureMinimum: {
                label: "Минимальный температурный режим, ℃",
                inputProps: { name: "temperatureMinimum", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
                validators: [
                    (value, data) => (data.temperatureRegime?.temperatureMaximum != null ? requiredField(value) : ""),
                ],
            },
            temperatureMaximum: {
                label: "Максимальный температурный режим, ℃",
                inputProps: { name: "temperatureMaximum", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
                validators: [
                    (value, data) => (data.temperatureRegime?.temperatureMinimum != null ? requiredField(value) : ""),
                ],
            },
        },
    },
    "expressBid.settings": {
        config: {
            averageSpeed: {
                label: "Средняя скорость",
                inputProps: { name: "averageSpeed", type: "number", step: "1" },
                required: true,
                validators: [(value, data) => (data.isExpressBid ? requiredField(value) : "")],
            },
            workHours: {
                label: "Кол-во рабочих часов",
                inputProps: { name: "workHours", type: "number", step: "1" },
                required: true,
                validators: [(value, data) => (data.isExpressBid ? requiredField(value) : "")],
            },
            dayStart: {
                label: "Начало дня",
                inputProps: { name: "dayStart", type: "time" },
                required: true,
                validators: [(value, data) => (data.isExpressBid ? requiredField(value) : "")],
            },
        },
    },
    expressBid: { value: { settings: {} } },
    emptyMileageBid: {
        config: {
            logisticReasonTypeId: {
                label: "Причина порожнего пробега",
                inputProps: { name: "logisticReasonTypeId" },
                dictionary: "emptyMileageBidReasonType",
                required: true,
                validators: [(value, data) => (data.emptyMileageBid ? requiredField(value) : "")],
            },
        },
    },
    isPreBid: {
        label: "Предзаявка",
        inputProps: { name: "isPreBid" },
    },
    legalPersonId: {
        // select
        label: "Организация",
        inputProps: { name: "legalPersonId" },
        propName: "name",
        url: legalPersons,
    },
    legalPersonBankAccountId: {
        label: "Банковский счет юридического лица",
        inputProps: { name: "legalPersonBankAccountId" },
    },
    responsibleId: {
        label: "Ответственный",
        inputProps: { name: "responsibleId" },
        url: logistsList,
        propName: "fullNameOrEmail",
    },
    salesManagerId: {
        label: "Менеджер по продажам",
        inputProps: { name: "salesManagerId" },
        url: listSalesManagers,
        propName: "fullNameOrEmail",
    },
    partnerId: {
        // async select
        label: "Грузоперевозчик",
        propName: "name",
        url: truckingPartnersList,
        inputProps: { name: "partnerId" },
    },
    cargoOwnerDictionaryItemId: {
        // async select
        label: "Контрагент",
        inputProps: { name: "cargoOwnerDictionaryItemId" },
        url: cargoOwnersList,
        components: {
            Option: CargoOwnerOption,
            ValueContainer: CargoOwnerValueContainer,
            MenuList: CargoOwnerMenuList,
        },
        filterPropName: ["name", "inn", "kpp"],
        propName: ({ name, inn, kpp }) => (inn || kpp ? `${name} (${compact([inn, kpp]).join("/")})` : name),
    },
    paymentTypeId: {
        // select
        label: "Тип оплаты",
        inputProps: { name: "paymentTypeId" },
        required: true,
        validators: [requiredField],
        dictionary: "paymentType",
    },
    ndsTypeId: {
        // select
        label: "НДС",
        inputProps: { name: "ndsTypeId" },
        required: true,
        validators: [requiredField],
        dictionary: "nDSType",
    },
    paymentPeriodType: {
        // select
        label: "Вид срока оплаты",
        inputProps: { name: "paymentPeriodType" },
        options: paymentPeriodTypes,
    },
    paymentPeriodInDays: {
        label: "Срок оплаты",
        inputProps: { name: "paymentPeriodInDays", type: "number", step: "1" },
    },
    price: {
        // field
        label: "Фиксированная цена",
        required: true,
        validators: [requiredField],
        inputProps: { name: "price", type: "number", step: "0.01" },
    },
    priceWithoutVatOnTop: {
        // field
        label: "Фиксированная цена",
        inputProps: { name: "priceWithoutVatOnTop", type: "number", step: "0.01" },
    },
    isVatTop: {
        // field
        label: "НДС сверху",
        inputProps: { name: "isVatTop" },
    },
    hourlyPrice: {
        // field
        label: "Почасовая цена",
        required: true,
        inputProps: { name: "hourlyPrice", type: "number", step: "1" },
    },
    invoiceTriggerType: {
        label: "Основание для выставления счёта",
        inputProps: { name: "invoiceTriggerType" },
        options: invoiceTriggerTypes,
        additionalText: (
            <span className="col-sm-1 col-md-1 col-1 text-center pl-0 col-form-label col-form-label-sm">
                <Tooltip
                    overlay="Нажмите для получения дополнительной информации"
                    overlayClassName="book-form__tooltip"
                >
                    <a
                        href="https://cargorun.omnidesk.ru/knowledge_base/item/366752?sid=77566"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="icon-uEA59-question book-form__tooltip-icon" />
                    </a>
                </Tooltip>
            </span>
        ),
    },
    comment: {
        // field
        label: "Комментарий",
        inputProps: { name: "comment" },
    },
    clientBidNumber: {
        // field
        label: "Номер заявки клиента",
        inputProps: { name: "clientBidNumber" },
    },
    clientBidDate: {
        // field
        label: "Дата заявки клиента",
        inputProps: { name: "clientBidDate", type: "date" },
    },
    payment: {
        config: {
            invoiceDate: {
                label: "Дата выставления счета",
                inputProps: { name: "invoiceDate", type: "date" },
            },
            planPaymentDate: {
                label: "Плановая дата платежа",
                inputProps: { name: "planPaymentDate", type: "date" },
                validators: [
                    (value, data) =>
                        data.payment?.paymentStatus && data.payment.paymentStatus !== bidPaymentStatusesEnum.NotPaid
                            ? requiredField(value)
                            : "",
                ],
            },
            factPaymentDate: {
                label: "Фактическая дата платежа",
                inputProps: { name: "factPaymentDate", type: "date" },
            },
            paymentStatus: {
                label: "Статус платежа",
                inputProps: { name: "paymentStatus" },
                options: bidPaymentStatuses,
                validators: [(value, data) => (data.payment?.updatedAt ? requiredField(value) : "")],
            },
            remainingPayment: {
                label: "Остаток к оплате",
                inputProps: { name: "remainingPayment", type: "number", step: "1" },
                validators: [
                    (value, data) =>
                        data.payment?.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid
                            ? requiredField(value)
                            : "",
                ],
            },
            comment: {
                label: "Комментарий",
                inputProps: { name: "comment" },
            },
            documentsTrackingStatus: {
                label: "Статус отслеживания документов",
                inputProps: { name: "documentsTrackingStatus" },
                options: documentsTrackingStatuses,
            },
            plannedArrivalDate: {
                label: "Плановая дата прибытия",
                inputProps: { name: "plannedArrivalDate", type: "date" },
            },
            documentsReceiptDate: {
                label: "Дата получения документов",
                inputProps: { name: "documentsReceiptDate", type: "date" },
                validators: [
                    (value, data) =>
                        data.payment?.documentsTrackingStatus === documentsTrackingStatusesEnum.Delivered
                            ? requiredField(value)
                            : "",
                ],
            },
            updatedAt: {},
        },
        value: {
            paymentStatus: bidPaymentStatusesEnum.NotPaid,
            documentsTrackingStatus: documentsTrackingStatusesEnum.NotShipped,
        },
    },
    cargos: {
        config: cargoConfig,
        value: [cargo],
    },
    expenses: {
        config: expensesСonfig,
        value: [],
    },
    typeOptions: {
        value: [],
        config: {
            id: {},
            entityOptionId: {},
        },
    },
    bidPoints: {
        value: [
            { ...point(0), type: bidPointTypesEnum.LoadPoint, order: 0 },
            { ...point(1), type: bidPointTypesEnum.UnloadPoint, order: 1 },
        ],
        config: {
            "geozone.address": {
                validators: [requiredField],
            },
            planEnterDate: {
                validators: [requiredField],
            },
            planLeaveDate: {
                validators: [
                    (value, data) => (bidPointTypesEnum.CustomPoint === data.type ? requiredField(value) : ""),
                ],
            },
            cargoOwnerDictionaryItemId: {
                // async select
                inputProps: { name: "cargoOwnerDictionaryItemId" },
                url: cargoOwnersList,
                components: {
                    Option: CargoOwnerOption,
                    ValueContainer: CargoOwnerValueContainer,
                    MenuList: CargoOwnerMenuList,
                },
                filterPropName: ["name", "inn", "kpp"],
                propName: ({ name, inn, kpp }) => (inn || kpp ? `${name} (${compact([inn, kpp]).join("/")})` : name),
            },
            externalCity: {
                // field
                label: "Город для 1С",
                inputProps: { name: "externalCity" },
            },
            comment: {},
            contactPerson: {},
            secondaryPlanEnterDate: {},
            counterpartyPointId: {},
            customPointTypeId: {
                validators: [
                    (value, data) => (bidPointTypesEnum.CustomPoint === data.type ? requiredField(value) : ""),
                ],
            },
            loadOptions: {},
        },
    },
    shareAccessEnabled: { label: "Предоставить доступ к заявке", inputProps: { name: "shareAccessEnabled" } },
    cargoOwnerShareAccess: { config: cargoOwnerConfig },
    "cargoOwnerShareAccess.contactPerson": { config: cargoOwnerContactPerson.config },
    logistTaskSettings: {
        config: {
            isActive: {
                bWrapper: "row",
                inputProps: { name: "isActive" },
                checkbox: true,
            },
            commentRequired: {
                bWrapper: "row",
                inputProps: { name: "commentRequired" },
                checkbox: true,
            },
            bidRole: {
                bWrapper: "row",
                inputProps: { name: "bidRole" },
                options: bidRoles,
                menuPortalTarget: document.body,
            },
            groupId: {
                bWrapper: "row",
                inputProps: { name: "groupId" },
                url: userGroupsList,
                propName: "name",
                menuPortalTarget: document.body,
            },
            id: {},
        },
        value: [],
    },
    forwardingOrder: {
        config: {
            fromPointPlanEnterTime: {
                inputProps: { name: "fromPointPlanEnterTime" },
                label: "Плановая дата",
                inPortal: true,
                libOptions: { minDate: new Date() },
                validators: [(value, data) => (data.forwardingOrder ? requiredField(value) : "")],
            },
            contactPersonFullName: {
                inputProps: { name: "contactPersonFullName" },
                label: "ФИО контактного лица",
                validators: [(value, data) => (data.forwardingOrder ? requiredField(value) : "")],
            },
            contactPersonPhoneNumber: {
                inputProps: { name: "contactPersonPhoneNumber" },
                label: "Телефон контактного лица",
                type: "phone",
                validators: [(value, data) => (data.forwardingOrder ? requiredField(value) : "")],
            },
            toPoint: {
                inputProps: { name: "toPoint" },
                label: "Желаемое направление",
            },
        },
    },
    documents: { value: [] },
    estimate: {}, // readonly attribute
    organizationId: {}, // readonly attribute
    truckingOrganizationId: {}, // readonly attribute
    type: {}, // readonly attribute
    status: {}, // readonly attribute,
    isAccepted: {}, // readonly attribute
    id: {}, // readonly attribute
    distributionBidId: {}, // readonly attribute
    isCompoundBid: {}, // readonly attribute
    routeCost: {}, // readonly attribute,
    plannedNumberOfDays: {}, // readonly attribute,
    estimatedNumberOfDays: {}, // readonly attribute
    version: {}, // readonly attribute
    sourceType: {}, // readonly attribute
    averageRateByDestination: {}, // readonly attribute, средняя ставка по направлению
};

export const emptyMileageBidDefaultValues = {
    paymentType: "Безналичный",
    ndsType: "20%",
    price: 1,
    cargoOwnerDictionaryItemId: null,
    cargoPackType: "",
    cargoName: "Порожняя заявка",
    cargoType: "Другое",
};

export const emptyMileageBidConfig = {
    "driver.id": { validators: [requiredField] },
    "carOption.carId": { validators: [requiredField] },
    bidPoints: {
        config: {
            "geozone.address": {
                validators: [requiredField],
            },
            planEnterDate: {
                validators: [requiredField],
            },
            planLeaveDate: {
                validators: [
                    (value, data) =>
                        [bidPointTypesEnum.Customs, bidPointTypesEnum.Border].includes(data.type)
                            ? requiredField(value)
                            : "",
                ],
            },
        },
    },
};

export default { cargoTruckConfig, cargoConfig, mainConfig };
